/**
 * Admin answer archive API helpers.
 *
 * Generates downloadable HTML ZIP archives for current answers and unsaved
 * re-analysis previews.
 */

import { apiPost } from './apiUtils';
import type { ReanalysisPayload } from './adminReanalysisApi';
import { saveBlobAsDownload } from './downloadUtils';

export { saveBlobAsDownload };

export type AnswerArchiveSource = 'current' | 'reanalysis_preview';
export type AnswerArchiveAudience = 'full' | 'examiner';

export interface AnswerArchiveDownloadRequest {
  readonly sessionId: string;
  readonly modules: readonly string[];
  readonly source?: AnswerArchiveSource;
  readonly audience?: AnswerArchiveAudience;
  readonly preview?: ReanalysisPayload;
  readonly metadata?: Record<string, unknown>;
  readonly filenameHint?: string;
}

function safeFilenameSegment(value: string | undefined, fallback: string): string {
  const normalized = (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[.-]+|[.-]+$/g, '');
  return normalized || fallback;
}

function defaultArchiveFilename(request: AnswerArchiveDownloadRequest): string {
  const session = safeFilenameSegment(request.sessionId, 'session');
  const source = safeFilenameSegment(request.source || 'current', 'current');
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const hint = request.filenameHint
    ? `${safeFilenameSegment(request.filenameHint, 'answers')}-`
    : '';
  const audience = request.audience === 'examiner' ? 'examiner-' : '';
  return `${audience}${hint}answers-${session}-${source}-${stamp}.zip`;
}

export async function requestAnswerHtmlArchive(
  request: AnswerArchiveDownloadRequest,
): Promise<Blob> {
  const blob = await apiPost<Blob>(
    `/admin/submissions/${encodeURIComponent(request.sessionId)}/html-archive`,
    {
      modules: request.modules,
      source: request.source || 'current',
      audience: request.audience || 'full',
      preview: request.preview,
      metadata: request.metadata || {},
    },
    {
      responseType: 'blob',
      errorMessage: 'Failed to download HTML archive',
      context: 'requestAnswerHtmlArchive',
      throwOnError: true,
    },
  );
  if (!blob) {
    throw new Error('Failed to download HTML archive');
  }
  return blob;
}

export async function downloadAnswerHtmlArchive(
  request: AnswerArchiveDownloadRequest,
): Promise<void> {
  const blob = await requestAnswerHtmlArchive(request);
  saveBlobAsDownload(blob, defaultArchiveFilename(request));
}
